import { useState, useCallback } from 'preact/hooks';
import { invalidateBarcode, flushAllCaches } from '../../api/cache';
import { reEvaluateProduct } from '../../api/products';

interface QuickActionsGridProps {
  onNavigate: (hash: string) => void;
}

interface ActionState {
  loading: boolean;
  message: string | null;
  error: boolean;
}

function SvgIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" dangerouslySetInnerHTML={{ __html: path }} />
  );
}

export function QuickActionsGrid({ onNavigate }: QuickActionsGridProps) {
  const [cacheBarcode, setCacheBarcode] = useState('');
  const [reEvalBarcode, setReEvalBarcode] = useState('');
  const [cacheState, setCacheState] = useState<ActionState>({ loading: false, message: null, error: false });
  const [reEvalState, setReEvalState] = useState<ActionState>({ loading: false, message: null, error: false });
  const [flushState, setFlushState] = useState<ActionState>({ loading: false, message: null, error: false });

  const handleInvalidateCache = useCallback(async () => {
    if (!cacheBarcode.trim()) return;
    setCacheState({ loading: true, message: null, error: false });
    try {
      const res = await invalidateBarcode(cacheBarcode.trim());
      setCacheState({ loading: false, message: `Cleared ${res.deleted} key(s)`, error: false });
      setCacheBarcode('');
    } catch (e) {
      setCacheState({ loading: false, message: e instanceof Error ? e.message : 'Failed', error: true });
    }
  }, [cacheBarcode]);

  const handleReEvaluate = useCallback(async () => {
    if (!reEvalBarcode.trim()) return;
    setReEvalState({ loading: true, message: null, error: false });
    try {
      await reEvaluateProduct(reEvalBarcode.trim());
      setReEvalState({ loading: false, message: 'Re-evaluated successfully', error: false });
      setReEvalBarcode('');
    } catch (e) {
      setReEvalState({ loading: false, message: e instanceof Error ? e.message : 'Failed', error: true });
    }
  }, [reEvalBarcode]);

  const handleFlushAll = useCallback(async () => {
    setFlushState({ loading: true, message: null, error: false });
    try {
      const res = await flushAllCaches();
      setFlushState({ loading: false, message: `Flushed ${res.deleted} key(s)`, error: false });
    } catch (e) {
      setFlushState({ loading: false, message: e instanceof Error ? e.message : 'Failed', error: true });
    }
  }, []);

  return (
    <div class="quick-actions-grid">
      {/* Invalidate Cache */}
      <div class="quick-action-card" onClick={(e) => {
        if ((e.target as HTMLElement).tagName !== 'INPUT' && (e.target as HTMLElement).tagName !== 'BUTTON') return;
      }}>
        <div class="quick-action-card-icon blue">
          <SvgIcon path='<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>' />
        </div>
        <div class="quick-action-card-title">Invalidate Cache</div>
        <div class="quick-action-card-desc">Clear cached data for a barcode</div>
        <div class="quick-action-form" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            placeholder="Barcode"
            value={cacheBarcode}
            onInput={(e) => setCacheBarcode((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleInvalidateCache(); }}
          />
          <button class="btn-primary btn-sm" onClick={handleInvalidateCache} disabled={cacheState.loading || !cacheBarcode.trim()}>
            {cacheState.loading ? 'Clearing...' : 'Clear'}
          </button>
        </div>
        {cacheState.message && (
          <div class={`msg ${cacheState.error ? 'msg-error' : 'msg-success'}`} style={{ marginTop: '4px', fontSize: '0.74rem', padding: '4px 8px' }}>
            {cacheState.message}
          </div>
        )}
      </div>

      {/* Re-Evaluate Product */}
      <div class="quick-action-card" onClick={(e) => {
        if ((e.target as HTMLElement).tagName !== 'INPUT' && (e.target as HTMLElement).tagName !== 'BUTTON') return;
      }}>
        <div class="quick-action-card-icon green">
          <SvgIcon path='<path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/><path d="M16 16h5v5"/>' />
        </div>
        <div class="quick-action-card-title">Re-Evaluate</div>
        <div class="quick-action-card-desc">Re-run verdicts for a product</div>
        <div class="quick-action-form" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            placeholder="Barcode"
            value={reEvalBarcode}
            onInput={(e) => setReEvalBarcode((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleReEvaluate(); }}
          />
          <button class="btn-primary btn-sm" onClick={handleReEvaluate} disabled={reEvalState.loading || !reEvalBarcode.trim()}>
            {reEvalState.loading ? 'Running...' : 'Run'}
          </button>
        </div>
        {reEvalState.message && (
          <div class={`msg ${reEvalState.error ? 'msg-error' : 'msg-success'}`} style={{ marginTop: '4px', fontSize: '0.74rem', padding: '4px 8px' }}>
            {reEvalState.message}
          </div>
        )}
      </div>

      {/* Flush All Caches */}
      <div class="quick-action-card" onClick={handleFlushAll}>
        <div class="quick-action-card-icon red">
          <SvgIcon path='<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>' />
        </div>
        <div class="quick-action-card-title">Flush All Caches</div>
        <div class="quick-action-card-desc">
          {flushState.loading ? 'Flushing...' : 'Delete all cached entries'}
        </div>
        {flushState.message && (
          <div class={`msg ${flushState.error ? 'msg-error' : 'msg-success'}`} style={{ marginTop: '4px', fontSize: '0.74rem', padding: '4px 8px' }}>
            {flushState.message}
          </div>
        )}
      </div>

      {/* View Errors */}
      <div class="quick-action-card" onClick={() => onNavigate('ops/logs')}>
        <div class="quick-action-card-icon amber">
          <SvgIcon path='<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>' />
        </div>
        <div class="quick-action-card-title">View Errors</div>
        <div class="quick-action-card-desc">Check error logs and rate limits</div>
      </div>

      {/* Browse Schema */}
      <div class="quick-action-card" onClick={() => onNavigate('ops/schema')}>
        <div class="quick-action-card-icon blue">
          <SvgIcon path='<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v4a9 3 0 0 0 18 0V5"/><path d="M3 9v4a9 3 0 0 0 18 0V9"/><path d="M3 13v4a9 3 0 0 0 18 0v-4"/>' />
        </div>
        <div class="quick-action-card-title">DB Schema</div>
        <div class="quick-action-card-desc">View database tables and columns</div>
      </div>

      {/* Version Settings */}
      <div class="quick-action-card" onClick={() => onNavigate('ops/version')}>
        <div class="quick-action-card-icon green">
          <SvgIcon path='<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>' />
        </div>
        <div class="quick-action-card-title">Version Settings</div>
        <div class="quick-action-card-desc">Manage app minimum versions</div>
      </div>
    </div>
  );
}
